import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { Button, Spinner, Alert } from 'react-bootstrap';
import managementStyles from '../ManagementPages.module.css';
import styles from './Onboarding.module.css';
import useAxios from '../../services/AxiosConfig';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { FileUploadIcon } from '../../components/icons/Icons';
import { refreshUserGroupsAndHierarchy } from '../../utils/sidebarUtils';
import {
  setOnboardingInProgress,
  resetOnboardingInProgress,
} from '../../slicers/OnboardingNavigationGuardSlice';

const POLL_INTERVAL_MS = 3000;
const EXCEL_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
};

const SUMMARY_COUNT_LABELS = [
  { key: 'organizations', label: 'Organizations' },
  { key: 'roles', label: 'Roles' },
  { key: 'groupAdmins', label: 'Group Admins' },
  { key: 'content', label: 'Content' },
  { key: 'doctors', label: 'Doctors' },
  { key: 'nurses', label: 'Nurses' },
  { key: 'staff', label: 'Staff' },
  { key: 'patients', label: 'Patients' },
  { key: 'totalUsers', label: 'Total users' },
];

const formatCountKeyAsLabel = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();

const Onboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const createdByAccountId = useSelector((state) => state.userInfo.id);
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const httpService = useAxios(axiosConfig.envURL);
  const pollingHttpService = useAxios(axiosConfig.envURL, true);

  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pollIntervalRef = useRef(null);
  const isTerminal = status === 'completed' || status === 'failed';
  const isInProgress = !!jobId && !isTerminal;

  // Access control: Super Admin only
  useEffect(() => {
    if (!isAdmin) {
      navigate('/organization-management', { replace: true });
    }
  }, [isAdmin, navigate]);

  const resetState = useCallback(() => {
    setFile(null);
    setJobId(null);
    setStatus(null);
    setError(null);
    setSummary(null);
    setIsSubmitting(false);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    setFile(acceptedFiles && acceptedFiles[0] ? acceptedFiles[0] : null);
    setError(null);
    setStatus(null);
    setSummary(null);
    setJobId(null);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: EXCEL_ACCEPT,
    onDrop,
    maxFiles: 1,
    disabled: !!jobId && status !== 'completed' && status !== 'failed',
  });

  const fetchJobStatus = useCallback(
    async (id) => {
      try {
        const response = await pollingHttpService.get(`onboarding/jobs/${id}`);
        const data = response.data?.data ?? response.data;
        setStatus(data?.status ?? null);
        setError(data?.error ?? null);
        setSummary(data?.summary ?? null);
        if (data?.status === 'completed' || data?.status === 'failed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch (err) {
        const res = err.response;
        if (res?.status === 404) {
          setError({ message: res?.data?.message || 'Onboarding job not found.' });
          setStatus('failed');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        } else {
          dispatch(
            showAlert({
              header: 'Error',
              message: res?.data?.message || 'Failed to fetch job status.',
              type: 'danger',
            })
          );
        }
      }
    },
    [pollingHttpService, dispatch]
  );

  useEffect(() => {
    dispatch(setOnboardingInProgress(isInProgress));
  }, [dispatch, isInProgress]);

  useEffect(() => {
    return () => {
      dispatch(resetOnboardingInProgress());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isInProgress) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isInProgress]);

  useEffect(() => {
    if (!jobId || status === 'completed' || status === 'failed') return;
    const id = setInterval(() => {
      fetchJobStatus(jobId);
    }, POLL_INTERVAL_MS);
    pollIntervalRef.current = id;
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [jobId, status, fetchJobStatus]);

  const handleSubmit = async () => {
    if (!file) return;
    setIsSubmitting(true);
    setError(null);
    setStatus(null);
    setSummary(null);
    try {
      const formData = new FormData();
      formData.append('workbook', file);
      if (createdByAccountId != null && createdByAccountId !== '') {
        formData.append('createdByAccountId', String(createdByAccountId));
      }
      const response = await httpService.request({
        method: 'POST',
        url: 'onboarding/workbooks',
        data: formData,
      });
      const body = response.data?.data ?? response.data;
      const acceptedStatus = response.status === 202 || response.status === 200;
      if (acceptedStatus && body?.jobId) {
        setJobId(body.jobId);
        setStatus(body.status || 'pending');
        setIsSubmitting(false);
        fetchJobStatus(body.jobId);
        refreshUserGroupsAndHierarchy(httpService, createdByAccountId, isAdmin, dispatch);
      } else {
        const serverMessage = body?.message || response.data?.message;
        dispatch(
          showAlert({
            header: 'Error',
            message: serverMessage || 'Unexpected response from server.',
            type: 'danger',
          })
        );
        setIsSubmitting(false);
      }
    } catch (err) {
      const res = err.response;
      const message =
        res?.data?.message ||
        (res?.status === 401
          ? 'Missing or invalid authentication.'
          : res?.status === 403
          ? 'Access denied. Super Admin only.'
          : 'Failed to upload workbook.');
      setError({ message });
      setStatus('failed');
      dispatch(
        showAlert({
          header: 'Error',
          message,
          type: 'danger',
        })
      );
      setIsSubmitting(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const isPolling = isInProgress;

  return (
    <div className={managementStyles.MainCard}>
      <h3 className={managementStyles.ContentTitle}>Select Organization Workbook</h3>
      <div className={styles.uploadSection}>
        <div
          {...getRootProps()}
          className={styles.dropzone}
          style={{ opacity: isPolling || isSubmitting ? 0.6 : 1 }}
        >
          <input {...getInputProps()} />
          <FileUploadIcon />
          <p className="subtitle-1" style={{ padding: 0, margin: 0 }}>
            Drop Excel file or browse
          </p>
          <span className="body-2">.xlsx or .xls</span>
          {file && (
            <p className={styles.fileName}>
              Selected: {file.name}
            </p>
          )}
        </div>
        
      </div>
      <div style={{ display: "flex", flex: "1" }}></div>
      <div className={managementStyles.ButtonsFooterContainer}>
          <Button
            variant="primary"
            className={managementStyles.CreateButton}
            onClick={handleSubmit}
            disabled={!file || isSubmitting || isPolling}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting…
              </>
            ) : (
              'Submit'
            )}
          </Button>
          {isTerminal && (
            <Button variant="outline-secondary" onClick={resetState} className="ms-2">
              {status === 'completed' ? 'Upload another' : 'Try again'}
            </Button>
          )}
        </div>

      {isPolling && (
        <Alert variant="info" className={styles.statusAlert}>
          <Spinner animation="border" size="sm" className="me-2" />
          {status === 'pending' ? 'Pending…' : 'Processing…'}
        </Alert>
      )}

      {status === 'completed' && summary && (
        <Alert variant="success" className={styles.statusAlert}>
          <strong>Completed</strong>
          <p className="mb-0 mt-1">{summary.message}</p>
          {summary.counts && (
            <div className={styles.counts}>
              <strong>Counts:</strong>
              <ul className="mb-0 mt-1">
                {summary.counts.hierarchy && (
                  <li>
                    Hierarchy: groups {summary.counts.hierarchy.groups}, subGroups{' '}
                    {summary.counts.hierarchy.subGroups}, teams {summary.counts.hierarchy.teams}
                  </li>
                )}
                {SUMMARY_COUNT_LABELS.map(
                  ({ key, label }) =>
                    summary.counts[key] != null && (
                      <li key={key}>
                        {label}: {summary.counts[key]}
                      </li>
                    )
                )}
                {Object.keys(summary.counts)
                  .filter(
                    (key) =>
                      key !== 'hierarchy' &&
                      !SUMMARY_COUNT_LABELS.some((e) => e.key === key) &&
                      summary.counts[key] != null &&
                      typeof summary.counts[key] !== 'object'
                  )
                  .map((key) => (
                    <li key={key}>
                      {formatCountKeyAsLabel(key)}: {summary.counts[key]}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </Alert>
      )}

      {status === 'failed' && error && (
        <Alert variant="danger" className={styles.statusAlert}>
          <strong>Failed</strong>
          <p className="mb-1 mt-1">{error.message}</p>
          {error.details &&
            (error.details.step ||
              (error.details.entityType != null && error.details.entityType !== '') ||
              (error.details.identifier != null && error.details.identifier !== '')) && (
              <div className={styles.details}>
                <strong>Details:</strong>
                <ul className="mb-0 mt-1">
                  {error.details.step && <li>Step: {error.details.step}</li>}
                  {error.details.entityType != null && error.details.entityType !== '' && (
                    <li>Entity type: {error.details.entityType}</li>
                  )}
                  {error.details.identifier != null && error.details.identifier !== '' && (
                    <li>Identifier: {error.details.identifier}</li>
                  )}
                </ul>
              </div>
            )}
        </Alert>
      )}
    </div>
  );
};

export default Onboarding;
